<template>
  <div>
    <SoPageHead :title="$t('securone.nav.documents')" :subtitle="$t('securone.documents.subtitle')">
      <select v-if="showDepartmentFilter" v-model="departmentId" class="sd-select sd-select--sm">
        <option value="">{{ $t("securone.department.filter_all") }}</option>
        <option v-for="d in departments" :key="d._id" :value="d._id">{{ d.name }}</option>
      </select>
      <SoSearch v-model="search" :placeholder="$t('securone.documents.search')" />
      <button v-if="canWrite.document" class="sd-btn sd-btn--success" @click="$refs.upload.openUpload()">{{ $t("securone.documents.upload") }}<BaseIcon icon="upload" class="ml-2" /></button>
    </SoPageHead>

    <SoNote :tone="canWrite.document ? 'muted' : 'info'" :icon="canWrite.document ? 'info-circle' : 'lock'" class="mb-4">
      {{ canWrite.document ? $t("securone.documents.level_admin_note") : $t("securone.documents.level_user_note") }}
    </SoNote>

    <div class="sd-card overflow-hidden">
      <div v-for="d in filtered" :key="d._id" class="border-b border-secondary-100 last:border-0">
        <button class="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 focus:outline-none" :class="{ 'bg-gray-50': open === d._id }" @click="open = open === d._id ? null : d._id">
          <div class="w-9 h-9 rounded bg-secondary-100 flex items-center justify-center text-secondary-600 flex-shrink-0"><BaseIcon icon="file-alt" /></div>
          <div class="flex-1 min-w-0">
            <div class="font-semibold text-gray-800 truncate">{{ d.title }}</div>
            <div class="text-xs text-gray-500 mt-0.5">
              <span class="font-mono">v{{ d.currentVersion }}</span> · <span class="font-mono">{{ dateStamp(current(d).uploadedAt) }}</span> · <span class="font-mono">{{ fileSize(current(d).size) }}</span>
              · <template v-if="d.attachedCategories.length">{{ $t("securone.documents.attached_to", { categories: d.attachedCategories.map((c) => c.name).join(", ") }) }}</template><template v-else>{{ $t("securone.documents.not_attached") }}</template>
            </div>
          </div>
          <!-- visibility chip reads All users / Customer users only / the department names (D-23) -->
          <SoChip>{{ levelLabel(d) }}</SoChip>
          <BaseIcon :icon="open === d._id ? 'chevron-up' : 'chevron-down'" class="text-gray-400 ml-2" />
        </button>
        <div v-if="open === d._id" class="px-4 pb-4 bg-gray-50">
          <div class="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1 pl-12">{{ $t("securone.documents.version_history") }}</div>
          <div class="pl-12">
            <div v-for="v in d.versions" :key="v.version" class="flex flex-wrap items-center gap-4 py-1.5 text-xs" :class="v.version === d.currentVersion ? 'text-gray-700' : 'text-gray-500'">
              <span class="font-mono w-8">v{{ v.version }}</span>
              <span class="font-mono w-24">{{ dateStamp(v.uploadedAt) }}</span>
              <span class="font-mono w-16">{{ fileSize(v.size) }}</span>
              <span class="truncate">{{ v.fileName }}</span>
              <span v-if="v.uploadedByName">{{ $t("securone.documents.published_by", { name: v.uploadedByName }) }}</span>
              <span v-if="v.version === d.currentVersion" class="text-secondary-700">{{ $t("securone.documents.current") }}</span>
              <button class="ml-auto text-primary-500 hover:underline" @click="openFile(d, v.version)">{{ $t("securone.documents.open") }}</button>
              <button class="text-primary-500 hover:underline" @click="openFile(d, v.version, true)">{{ $t("securone.documents.download") }}</button>
            </div>
          </div>
          <div class="flex flex-wrap items-center gap-2 mt-3 pl-12">
            <button class="sd-btn sd-btn--secondary sd-btn--sm" @click="openFile(d, 'current')"><BaseIcon icon="book-open" class="mr-1" />{{ $t("securone.documents.open_version", { n: d.currentVersion }) }}</button>
            <template v-if="d.canEdit">
              <button class="sd-btn sd-btn--secondary sd-btn--sm" @click="$refs.upload.openVersion(d)"><BaseIcon icon="upload" class="mr-1" />{{ $t("securone.documents.new_version") }}</button>
              <button class="sd-btn sd-btn--secondary sd-btn--sm" @click="$refs.edit.open(d)">{{ $t("edit") }}<BaseIcon icon="pencil-alt" class="ml-1" /></button>
              <button class="sd-btn sd-btn--danger sd-btn--sm" @click="confirmDelete(d)"><BaseIcon icon="trash" class="mr-1" />{{ $t("delete") }}</button>
            </template>
            <span class="text-xs text-gray-500 ml-2">{{ $t("securone.documents.recipients_get_latest", { n: d.currentVersion }) }}</span>
          </div>
        </div>
      </div>
      <div v-if="!filtered.length" class="px-4 py-8 text-center text-gray-400 text-sm">{{ $t("securone.documents.none") }}</div>
    </div>

    <UploadDocumentModal ref="upload" @saved="onSaved" />
    <EditDocumentModal ref="edit" @saved="load" />
  </div>
</template>

<script>
import SoPageHead from "./components/SoPageHead.vue";
import SoSearch from "./components/SoSearch.vue";
import SoChip from "./components/SoChip.vue";
import SoNote from "./components/SoNote.vue";
import UploadDocumentModal from "./components/UploadDocumentModal.vue";
import EditDocumentModal from "./components/EditDocumentModal.vue";
import api from "./securone.api";
import { dateStamp, fileSize, errorMessage, scopeLabel, escapeHtml } from "./securone.helpers";

// D-23 / D-34 / D-39. The library is scoped on read (V9): a customer-only
// document is absent from a department or basic user's response, not hidden.
export default {
  name: "SecurOneDocuments",
  components: { SoPageHead, SoSearch, SoChip, SoNote, UploadDocumentModal, EditDocumentModal },
  data() {
    return { documents: [], search: "", open: null, departmentId: this.$store.getters["securone/departmentFilter"] || "" };
  },
  computed: {
    canWrite() {
      return this.$store.getters["securone/canWrite"];
    },
    departments() {
      return this.$store.getters["securone/departments"];
    },
    showDepartmentFilter() {
      return this.$store.getters["securone/showDepartmentFilter"];
    },
    adminVersion() {
      return this.$store.state.securone.adminVersion;
    },
    filtered() {
      const q = this.search.trim().toLowerCase();
      return q ? this.documents.filter((d) => [d.title, ...d.departmentNames, ...d.attachedCategories.map((c) => c.name)].join(" ").toLowerCase().includes(q)) : this.documents;
    },
  },
  methods: {
    dateStamp,
    fileSize,
    levelLabel(d) {
      return scopeLabel(this, d.level, d.departmentNames);
    },
    current(d) {
      return d.versions.find((v) => v.version === d.currentVersion) || d.versions[0] || {};
    },
    async load() {
      try {
        this.documents = await api.documents({ departmentId: this.departmentId || undefined });
      } catch (error) {
        this.handleError(error);
      }
    },
    async openFile(d, version, download = false) {
      try {
        const file = await api.documentFile(d._id, version, download);
        window.open(file.url, "_blank", "noopener");
      } catch (error) {
        this.handleError(error, errorMessage(this, error));
      }
    },
    onSaved(doc) {
      this.open = doc._id;
      this.$toasted.show(this.$t("securone.documents.saved_toast", { n: doc.currentVersion }), { type: "success", theme: "outline", position: "top-right", duration: 6000 });
      this.load();
    },
    confirmDelete(d) {
      const attached = d.attachedCategories.map((c) => c.name).join(", ");
      this.$modal.show("dialog", {
        title: `<span class="text-red-500">${this.$t("securone.documents.delete_title", { name: escapeHtml(d.title) })}</span>`,
        text: `<div class="text-sm text-gray-700 text-left">${this.$t("securone.documents.delete_consequence", { n: d.versions.length })} ${attached ? this.$t("securone.documents.delete_attached", { categories: escapeHtml(attached) }) : this.$t("securone.documents.delete_not_attached")}</div>`,
        buttons: [
          { title: `<div class="bg-success-500 text-white text-sm font-sans py-2">${this.$t("securone.common.cancel")}</div>`, handler: () => this.$modal.hide("dialog") },
          { title: `<div class="bg-red-500 text-white text-sm font-sans py-2">${this.$t("delete")}</div>`, handler: async () => {
            this.$modal.hide("dialog");
            try {
              await api.deleteDocument(d._id);
              this.$toasted.show(this.$t("securone.documents.deleted_toast", { name: d.title }), { type: "success", theme: "outline", position: "top-right", duration: 5000 });
              this.load();
            } catch (error) {
              this.handleError(error, errorMessage(this, error));
            }
          } },
        ],
      });
    },
  },
  watch: {
    adminVersion() {
      this.load();
    },
    departmentId(value) {
      this.$store.commit("securone/SET_DEPARTMENT_FILTER", value || null);
      this.load();
    },
  },
  created() {
    this.setPageTitle("SecurOne · " + this.$t("securone.nav.documents"), "securone_documents");
    this.load();
  },
};
</script>
